// roomActionsParity — the WolfChat parity write/read helpers (FEATURES.chatParity).
//
// These live in their own file, not in services/roomActions.ts, for one
// reason: the backend workstream is building the SQL + the same helpers on a
// parallel branch (see /Users/apple/steven/WOLFCHAT_CONTRACT.md). The names and
// signatures here are the contract's, so merging is a delete of this file and a
// re-point of the imports — not a three-way merge inside a 600-line module.
//
// EVERY function degrades. The migrations (0056–0062) are applied by Umer, by
// hand, after this ships; a client with the flag on and the schema not yet
// applied must not throw into the UI. So:
//   • an RPC that does not exist (PostgREST PGRST202) falls back to the direct
//     write the pre-parity client already did, and
//   • a table that does not exist (42P01) reads as empty.
// `isMissingSchema` is the single place that decides what "not applied yet"
// looks like, so the fallbacks cannot drift apart.

import { supabase, isoFromMs, msFromISO0, mustAffectRows } from './supabaseClient';
import type { RoomAttachment, RoomMention, RoomMessage, RoomPin, UserRole } from '../types';

/** PostgREST's "the function/table/column isn't there" family. Anything else is
 *  a real error and must surface — an RLS denial must never look like a missing
 *  migration, or a blocked write would silently take the fallback path. */
export function isMissingSchema(e: { code?: string; message?: string } | null | undefined): boolean {
  if (!e) return false;
  const code = e.code ?? '';
  if (code === 'PGRST202' || code === 'PGRST204') return true; // no such function / no such column
  if (code === '42883' || code === '42P01' || code === '42703') return true; // undefined function / table / column
  const m = (e.message ?? '').toLowerCase();
  return (
    m.includes('could not find the function') ||
    m.includes('does not exist') && (m.includes('function') || m.includes('relation') || m.includes('column'))
  );
}

export interface RoomSender { id: string; name: string; role: UserRole }

export interface SendRoomMessageV2Input {
  roomId: string;
  /** Client-minted final id (the optimistic bubble already shows it). */
  id: string;
  body: string;
  sender: RoomSender;
  /** Epoch ms, stamped by the composer. See the keyset INVARIANT in chat/window.ts. */
  at: number;
  replyToId?: string;
  /** `@user` / `@all` / `@order` rows for room_message_mentions. */
  mentions?: RoomMention[];
  /** Rows for room_attachments, already uploaded. */
  attachments?: Array<Partial<RoomAttachment>>;
  /** Legacy columns kept for flag-off clients that only understand these. */
  imageUrl?: string;
  imageThumbUrl?: string;
  imagePath?: string;
  /** Legacy order-only mentions jsonb, also kept for flag-off clients. */
  legacyMentions?: Array<{ orderId: string; orderNumber: string }>;
}

/**
 * Send through the `send_room_message` RPC (idempotent on p_id, writes the
 * mention + attachment rows and the reply snapshot in one transaction), falling
 * back to the plain INSERT the pre-parity client does.
 *
 * The fallback deliberately drops the mention/attachment ROWS (there are no
 * tables to put them in) but keeps everything that lives on `room_messages`
 * itself, so a message sent against an un-migrated database is a normal message
 * rather than a lost one.
 */
export async function sendRoomMessageV2(input: SendRoomMessageV2Input): Promise<void> {
  const sb = supabase();
  const createdAt = isoFromMs(input.at);
  const { error } = await sb.rpc('send_room_message', {
    p_room_id: input.roomId,
    p_id: input.id,
    p_body: input.body,
    p_reply_to_id: input.replyToId ?? null,
    p_mentions: input.mentions?.length ? input.mentions : null,
    p_attachments: input.attachments?.length ? input.attachments : null,
    p_created_at: createdAt,
  });
  if (!error) return;
  if (!isMissingSchema(error)) throw new Error(`send_room_message: ${error.message}`);

  const row: Record<string, unknown> = {
    id: input.id,
    room_id: input.roomId,
    body: input.body,
    created_at: createdAt,
    sender_id: input.sender.id,
    sender_name: input.sender.name,
    sender_role: input.sender.role,
  };
  if (input.replyToId) row.reply_to_id = input.replyToId;
  if (input.imageUrl) row.image_url = input.imageUrl;
  if (input.imageThumbUrl) row.image_thumb_url = input.imageThumbUrl;
  if (input.imagePath) row.image_path = input.imagePath;
  if (input.legacyMentions?.length) row.mentions = input.legacyMentions;
  const { error: insErr } = await sb.from('room_messages').insert(row);
  if (insErr) throw new Error(`room_messages insert: ${insErr.message}`);
}

/**
 * Toggle my reaction via the `toggle_reaction` RPC. Returns the emoji now on
 * the message for me, or null when it was cleared. `fallback` is the existing
 * jsonb read-modify-write (services/roomActions.setMessageReaction) — passed in
 * rather than imported so this module keeps no dependency on the legacy path's
 * notification side effects.
 */
export async function toggleReaction(
  messageId: string,
  emoji: string,
  fallback: () => Promise<void>,
): Promise<string | null> {
  const { data, error } = await supabase().rpc('toggle_reaction', {
    p_message_id: messageId,
    p_emoji: emoji,
  });
  if (!error) return (data as { emoji?: string } | null)?.emoji ?? null;
  if (!isMissingSchema(error)) throw new Error(`toggle_reaction: ${error.message}`);
  await fallback();
  return emoji;
}

/** What `room_sync_since` hands back: the raw rows (snake_case, ISO dates)
 *  changed since the cursor, in the same shape realtime payloads carry — which
 *  is the point: a catch-up row goes through the caller's normal patch path,
 *  never through a list rebuild. */
export interface RoomSyncSinceResult {
  /**
   * The SERVER's clock at the moment of the read — the ONLY safe cursor for
   * the next poll. `p_since` is compared against `created_at`, which is the
   * server's clock; a browser clock a minute fast would otherwise ask for the
   * future and catch up on nothing, for as long as the tab stays open. 0 when
   * the RPC gave no usable `at` (then the caller falls back to its own clock).
   */
  at: number;
  messages: any[];
  /**
   * EVERY room asked about, changed or not — `room_sync_since` does not filter
   * this half by the cursor. So a non-empty `rooms` means nothing on its own;
   * "did the list move?" has to be a comparison of the payload against the
   * previous one (see the rooms-list poll in context/AppContext.tsx).
   */
  rooms: any[];
}

/**
 * Catch-up read for the polling fallback (services/chat/liveness.ts): every
 * `room_messages` row in `roomIds` that changed since `sinceMs`, plus the
 * list row of every room asked about.
 *
 * "Changed" means an insert, an edit or a soft delete (the RPC tests
 * `created_at` / `edited_at` / `deleted_at`), so applying the rows as UPDATEs
 * lands what those missed realtime payloads would have. A REACTION is the one
 * change it cannot see: the 0056 mirror trigger rewrites `room_messages.
 * reactions` without touching any of those three stamps. Reactions therefore
 * still need the live channel (or the next real edit to that row) — do not
 * promise otherwise in the UI.
 *
 * Returns null when 0060 is not applied — after running `fallback`, which is
 * the caller's existing full refetch. That keeps production (no 0056–0063)
 * working: the fallback is the pre-parity behaviour, throttled by the same
 * backoff.
 */
export async function roomSyncSince(
  roomIds: string[],
  sinceMs: number,
  fallback: () => void | Promise<void>,
): Promise<RoomSyncSinceResult | null> {
  if (!roomIds.length) return { at: 0, messages: [], rooms: [] };
  const { data, error } = await supabase().rpc('room_sync_since', {
    p_room_ids: roomIds,
    p_since: isoFromMs(sinceMs),
  });
  if (error) {
    if (!isMissingSchema(error)) throw new Error(`room_sync_since: ${error.message}`);
    await fallback();
    return null;
  }
  const payload = (data ?? {}) as { at?: unknown; messages?: unknown; rooms?: unknown };
  const at = typeof payload.at === 'string' ? msFromISO0(payload.at) : 0;
  return {
    at: Number.isFinite(at) ? at : 0,
    messages: Array.isArray(payload.messages) ? payload.messages : [],
    rooms: Array.isArray(payload.rooms) ? payload.rooms : [],
  };
}

/**
 * A stable fingerprint of `room_sync_since`'s `rooms` half — the fields the
 * rooms LIST actually renders.
 *
 * Needed because that half is NOT filtered by the cursor: the RPC returns every
 * room asked about on every call, so "did the list move?" cannot be a length
 * check (it would be true forever, and the rooms list would run a full
 * rooms+room_members refetch on every poll, on every signed-in tab, for as long
 * as a socket stayed down). Comparing this string against the previous poll's
 * is the real test: a rename or a preview bump changes it, a quiet fleet
 * does not.
 */
export function roomsListSignature(rooms: any[]): string {
  return (Array.isArray(rooms) ? rooms : [])
    .map((r) => [r?.id, r?.name, r?.last_message_at, r?.last_message_body, r?.last_message_sender_id].join('|'))
    .sort()
    .join('\n');
}

export interface ReactionActor { userId: string; name: string; emoji: string; at?: number }

/**
 * Who reacted with what, for the who-reacted popover. Reads the normalized
 * `room_message_reactions` table joined to `users`; when that table is not
 * there yet the caller's jsonb map is the only source, so this returns [] and
 * the popover falls back to resolving uids against the AppContext user list.
 */
export async function fetchReactionActors(messageId: string): Promise<ReactionActor[]> {
  const { data, error } = await supabase()
    .from('room_message_reactions')
    .select('user_id, emoji, created_at, users(id, name)')
    .eq('message_id', messageId);
  if (error) {
    if (isMissingSchema(error)) return [];
    throw new Error(`room_message_reactions: ${error.message}`);
  }
  return (data || []).map((r: any) => ({
    userId: String(r.user_id),
    // Empty, not 'Someone', when the join found no user row: the placeholder is
    // the UI's job, and a literal 'Someone' here would beat the real name the
    // AppContext user list can still supply (services/reactionActors.ts).
    name: r.users?.name || '',
    emoji: String(r.emoji),
    at: r.created_at ? msFromISO0(r.created_at) : undefined,
  }));
}

/**
 * My `room_member_state` row. `lastReadAt` is SNAPSHOT ONCE when the room opens
 * and drives the unread divider — read it again and the divider walks up the
 * screen while the reader is looking at it.
 */
export interface RoomMemberState { lastReadAt: number | null; mutedUntil: number | null }
export async function fetchRoomMemberState(roomId: string): Promise<RoomMemberState> {
  const { data, error } = await supabase()
    .from('room_member_state')
    .select('last_read_at, muted_until')
    .eq('room_id', roomId)
    .maybeSingle();
  if (error) {
    if (isMissingSchema(error)) return { lastReadAt: null, mutedUntil: null };
    throw new Error(`room_member_state: ${error.message}`);
  }
  return {
    lastReadAt: data?.last_read_at ? msFromISO0(data.last_read_at) : null,
    mutedUntil: data?.muted_until ? msFromISO0(data.muted_until) : null,
  };
}

/**
 * Mute (a future instant) or unmute (null) this room for me.
 *
 * UPSERT, not UPDATE: `room_member_state` rows are seeded by `mark_room_read`
 * (0054) and by 0059's member trigger, so a member who joined before 0059 and
 * whose client never ran the cursor write (chatEngineV2 off — the flag-on-but-
 * engine-off shape that staging runs today) has NO row. A bare UPDATE there
 * matches nothing, returns no error, and the mute is silently lost while the
 * UI says "Group muted". The upsert creates the row; `mustAffectRows` makes an
 * RLS refusal loud instead of invisible.
 *
 * `userId` is the app user id (`users.id` = `app_uid()`), needed for the
 * conflict target and for the RLS `with check (user_id = app_uid())`.
 */
export async function setRoomMute(
  roomId: string,
  untilMs: number | null,
  userId?: string | null,
): Promise<void> {
  const muted_until = untilMs == null ? null : isoFromMs(untilMs);
  if (!userId) {
    // No id to write: fall back to the narrow UPDATE, but still refuse to
    // report success when it touched nothing.
    await mustAffectRows('room_member_state mute', () =>
      supabase().from('room_member_state').update({ muted_until }).eq('room_id', roomId),
    );
    return;
  }
  await mustAffectRows('room_member_state mute', () =>
    supabase().from('room_member_state').upsert(
      { room_id: roomId, user_id: userId, muted_until, updated_at: new Date().toISOString() },
      { onConflict: 'room_id,user_id' },
    ),
  );
}

/** In-room search. Server-side (tsvector/trgm) so it reaches messages the
 *  client window never loaded, which is the whole point of the feature. */
export async function searchRoomMessages(
  roomId: string,
  query: string,
  opts: { limit?: number; beforeAt?: number | null; beforeId?: string | null } = {},
): Promise<any[]> {
  const q = query.trim();
  if (!q) return [];
  const sb = supabase();
  const { data, error } = await sb.rpc('search_room_messages', {
    p_room_id: roomId,
    p_query: q,
    p_limit: opts.limit ?? 40,
    p_before_at: opts.beforeAt ? isoFromMs(opts.beforeAt) : null,
    p_before_id: opts.beforeId ?? null,
  });
  if (!error) return (data || []) as any[];
  if (!isMissingSchema(error)) throw new Error(`search_room_messages: ${error.message}`);
  // Fallback: ILIKE straight at the table. Slower and accent-naive, but it
  // keeps the search box working before 0056 lands.
  const esc = q.replace(/[%_\\]/g, (c) => `\\${c}`);
  const { data: rows, error: selErr } = await sb
    .from('room_messages')
    .select('*')
    .eq('room_id', roomId)
    .ilike('body', `%${esc}%`)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(opts.limit ?? 40);
  if (selErr) throw new Error(`room_messages search: ${selErr.message}`);
  return (rows || []) as any[];
}

/** Pin durations offered in the UI (Umer, 2026-09-14): 24 h / 7 d / 30 d. */
export const PIN_DURATIONS = [
  { label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: '7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { label: '30 days', ms: 30 * 24 * 60 * 60 * 1000 },
] as const;
/** WhatsApp's default, and ours. */
export const PIN_DEFAULT_MS = PIN_DURATIONS[1].ms;
/** WhatsApp's cap, and ours. */
export const MAX_PINS_PER_ROOM = 3;

/** Live (unexpired) pins for a room, newest first. */
export async function fetchRoomPins(roomId: string, nowMs: number = Date.now()): Promise<RoomPin[]> {
  const { data, error } = await supabase()
    .from('room_pins')
    .select('*')
    .eq('room_id', roomId)
    .order('pinned_at', { ascending: false });
  if (error) {
    if (isMissingSchema(error)) return [];
    throw new Error(`room_pins: ${error.message}`);
  }
  return (data || [])
    .map((r: any): RoomPin => ({
      roomId: String(r.room_id),
      messageId: String(r.message_id),
      pinnedById: r.pinned_by_id ?? undefined,
      pinnedByName: r.pinned_by_name ?? 'Someone',
      pinnedAt: msFromISO0(r.pinned_at),
      expiresAt: r.expires_at ? msFromISO0(r.expires_at) : undefined,
    }))
    // Expiry is enforced here as well as server-side: a tab left open past an
    // expiry must stop showing the bar without waiting for a refetch.
    .filter((p) => p.expiresAt == null || p.expiresAt > nowMs);
}

export async function pinMessage(
  roomId: string,
  messageId: string,
  by: { id: string; name: string },
  durationMs: number = PIN_DEFAULT_MS,
): Promise<void> {
  const now = Date.now();
  const { error } = await supabase().from('room_pins').upsert(
    {
      room_id: roomId,
      message_id: messageId,
      pinned_by_id: by.id,
      pinned_by_name: by.name,
      pinned_at: isoFromMs(now),
      expires_at: isoFromMs(now + durationMs),
    },
    { onConflict: 'room_id,message_id' },
  );
  if (error) throw new Error(`room_pins upsert: ${error.message}`);
}

export async function unpinMessage(roomId: string, messageId: string): Promise<void> {
  await mustAffectRows('room_pins delete', () =>
    supabase().from('room_pins').delete().eq('room_id', roomId).eq('message_id', messageId),
  );
}

/**
 * Attachments for a room's media gallery, newest first (keyset by created_at,
 * `id desc` as the tiebreak the 0056 index carries).
 *
 * `beforeAt` is INCLUSIVE on purpose: every attachment of one message is
 * written by a single insert, so a five-photo message gives five rows with the
 * IDENTICAL created_at. An exclusive `<` cursor would skip the rest of that
 * group forever whenever a page boundary landed inside it. The caller pages by
 * the oldest row it holds and de-duplicates by id (MediaGallery does; the
 * repeated boundary rows are the price of never losing one).
 */
export async function fetchRoomAttachments(
  roomId: string,
  opts: { limit?: number; beforeAt?: number | null } = {},
): Promise<Array<RoomAttachment & { createdAt: number }>> {
  let q = supabase()
    .from('room_attachments')
    .select('*')
    .eq('room_id', roomId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(opts.limit ?? 60);
  if (opts.beforeAt) q = q.lte('created_at', isoFromMs(opts.beforeAt) as string);
  const { data, error } = await q;
  if (error) {
    if (isMissingSchema(error)) return [];
    throw new Error(`room_attachments: ${error.message}`);
  }
  return (data || []).map((r: any) => ({
    id: String(r.id),
    messageId: String(r.message_id),
    roomId: String(r.room_id),
    kind: r.kind === 'file' ? 'file' : 'image',
    mime: r.mime ?? 'application/octet-stream',
    byteSize: Number(r.byte_size) || 0,
    originalPath: r.original_path ?? '',
    displayPath: r.display_path ?? undefined,
    thumbPath: r.thumb_path ?? undefined,
    width: r.width ?? undefined,
    height: r.height ?? undefined,
    fileName: r.file_name ?? undefined,
    caption: r.caption ?? undefined,
    displayUrl: r.display_url ?? undefined,
    sortOrder: Number(r.sort_order) || 0,
    createdAt: msFromISO0(r.created_at),
  }));
}

/** Every room message that `@`-mentions this order — the "Mentioned in" list in
 *  the order detail panel. Reads the `order_message_references` view. */
export interface OrderMessageReference {
  messageId: string;
  roomId: string;
  roomName?: string;
  body: string;
  senderName: string;
  createdAt: number;
}
export async function fetchOrderMessageReferences(orderId: string, limit = 20): Promise<OrderMessageReference[]> {
  const { data, error } = await supabase()
    .from('order_message_references')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    if (isMissingSchema(error)) return [];
    throw new Error(`order_message_references: ${error.message}`);
  }
  return (data || []).map((r: any) => ({
    messageId: String(r.message_id),
    roomId: String(r.room_id),
    roomName: r.room_name ?? undefined,
    body: r.body ?? '',
    senderName: r.sender_name ?? '',
    createdAt: msFromISO0(r.created_at),
  }));
}

/** Link-preview cards for the urls in the loaded window. Keyed by url_hash. */
export async function fetchLinkPreviews(urlHashes: string[]): Promise<Map<string, import('../types').LinkPreview>> {
  const out = new Map<string, import('../types').LinkPreview>();
  const hashes = Array.from(new Set(urlHashes)).filter(Boolean);
  if (!hashes.length) return out;
  const { data, error } = await supabase().from('link_previews').select('*').in('url_hash', hashes);
  if (error) {
    if (isMissingSchema(error)) return out;
    throw new Error(`link_previews: ${error.message}`);
  }
  for (const r of data || []) {
    out.set(String(r.url_hash), {
      urlHash: String(r.url_hash),
      url: String(r.url),
      status: (r.status as any) ?? 'pending',
      title: r.title ?? undefined,
      description: r.description ?? undefined,
      siteName: r.site_name ?? undefined,
      imageUrl: r.image_url ?? undefined,
    });
  }
  return out;
}

/** room_messages row → RoomMessage, including every parity column. One mapper
 *  so RoomChat, the search results list and the pinned bar agree on shape. */
export function mapRoomMessageRow(r: any): RoomMessage {
  return {
    id: r.id,
    roomId: r.room_id,
    body: r.body ?? '',
    imageUrl: r.image_url ?? undefined,
    imageThumbUrl: r.image_thumb_url ?? undefined,
    imagePath: r.image_path ?? undefined,
    timestamp: msFromISO0(r.created_at),
    senderId: r.sender_id ?? '',
    senderName: r.sender_name ?? '',
    senderRole: r.sender_role,
    mentions: r.mentions ?? undefined,
    reactions: r.reactions ?? undefined,
    edited: r.edited || undefined,
    editedAt: r.edited_at ? msFromISO0(r.edited_at) : undefined,
    deleted: r.deleted || undefined,
    deletedAt: r.deleted_at ? msFromISO0(r.deleted_at) : undefined,
    deletedById: r.deleted_by_id ?? undefined,
    deletedByName: r.deleted_by_name ?? undefined,
    kind: r.kind === 'system' ? 'system' : undefined,
    systemEvent: r.system_event ?? undefined,
    replyToId: r.reply_to_id ?? undefined,
    replySnapshot: r.reply_snapshot ?? undefined,
    clientId: r.client_id ?? undefined,
    hasAttachment: r.has_attachment || undefined,
  };
}

/** One row of the unread summary. `mentionCount` and `muted` arrive only from
 *  `room_unread_summary_v2()` (migration 0060); the v1 fallback leaves them at
 *  0 / false so every caller can read them unconditionally. */
export interface RoomUnreadRow {
  roomId: string;
  unread: number;
  mentionCount: number;
  muted: boolean;
}

/**
 * Per-room unread + mention counts + mute state in ONE round trip.
 *
 * Tries `room_unread_summary_v2()` first and falls back to the 2-column
 * `room_unread_summary()` that has been live since 0054 — the contract keeps
 * both on purpose so a client built against v2 still works on a database that
 * only has v1 (and so an old client keeps working after 0060).
 */
export async function fetchRoomUnreadSummaryV2(): Promise<RoomUnreadRow[]> {
  const sb = supabase();
  const { data, error } = await sb.rpc('room_unread_summary_v2');
  if (!error) {
    return (data || []).map((r: any): RoomUnreadRow => ({
      roomId: String(r.room_id),
      unread: Number(r.unread) || 0,
      mentionCount: Number(r.mention_count) || 0,
      muted: !!r.muted,
    }));
  }
  if (!isMissingSchema(error)) throw new Error(`room_unread_summary_v2: ${error.message}`);
  const { data: v1, error: v1Err } = await sb.rpc('room_unread_summary');
  if (v1Err) throw new Error(`room_unread_summary: ${v1Err.message}`);
  return (v1 || []).map((r: any): RoomUnreadRow => ({
    roomId: String(r.room_id),
    unread: Number(r.unread) || 0,
    mentionCount: 0,
    muted: false,
  }));
}

/** My mute state for every room I'm a member of, keyed by room id. Empty when
 *  0054's `room_member_state` is not there (or RLS hides it) — "not muted" is
 *  the safe default: a missed suppression is noise, a wrong one is a missed
 *  message. */
export async function fetchMyRoomMuteStates(): Promise<Record<string, number>> {
  const { data, error } = await supabase()
    .from('room_member_state')
    .select('room_id, muted_until');
  if (error) {
    if (isMissingSchema(error)) return {};
    throw new Error(`room_member_state: ${error.message}`);
  }
  const out: Record<string, number> = {};
  for (const r of data || []) {
    if (r?.muted_until) out[String(r.room_id)] = msFromISO0(r.muted_until);
  }
  return out;
}

/** `room_message_mentions` rows for one message. `null` (not []) when the table
 *  is unreadable, so the caller knows to fall back to parsing the body rather
 *  than concluding "nobody was mentioned". */
export async function fetchMessageMentions(
  messageId: string,
): Promise<Array<{ kind?: string; user_id?: string | null }> | null> {
  const { data, error } = await supabase()
    .from('room_message_mentions')
    .select('kind, user_id')
    .eq('message_id', messageId);
  if (error) return null;
  return (data || []) as Array<{ kind?: string; user_id?: string | null }>;
}
